import React, { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box, Paper, TextField, Button, Alert, CircularProgress, Typography,
  IconButton, InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import axios from '../config/axios';
import { API_ENDPOINTS } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import { BRAND, TEXT } from '../theme/colors';

const ResetPassword = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  // An invitation link (myproject/invites.py) carries ?welcome=1: greet the
  // person rather than show a forgotten-password screen (walk 2026-10-09).
  const welcome = new URLSearchParams(location.search).get('welcome') === '1';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setStatus('error');
      setMessage(t('passwordsDoNotMatch') || 'Passwords do not match.');
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(API_ENDPOINTS.AUTH_RESET_PASSWORD, { uid, token, password });
      setStatus('success');
      // Our own sentence: the server's is English only.
      setMessage(welcome
        ? (t('welcomePasswordSet') || 'Your password is set. Sign in with your email and this password.')
        : (t('passwordResetSuccess') || 'Password reset successful. You can now log in.'));
      setTimeout(() => navigate('/login'), welcome ? 4000 : 2500);
    } catch (err) {
      setStatus('error');
      const detail = err.response?.data?.detail;
      const expired = !detail || /invalid or expired/i.test(detail);
      setMessage(expired
        ? (welcome
          ? (t('welcomeLinkExpired') || 'This invitation link has expired. Ask the administrator for a new one.')
          : (t('passwordResetFailed') || 'Invalid or expired reset link. Please request a new one.'))
        : detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      px: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${BRAND.brownDark} 0%, ${BRAND.green} 50%, ${BRAND.brownMed} 100%)`,
    }}>
      <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 420, width: '100%', borderRadius: 3, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src={language === 'el' ? '/light_gr.png' : '/light_en.png'}
            alt="TerraMind"
            sx={{ maxWidth: 340, height: 90, objectFit: 'contain', display: 'block', mx: 'auto', mb: 1.5 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, color: TEXT.primary }}>
            {welcome ? (t('welcomeSetPasswordTitle') || 'Welcome to TerraMind') : (t('resetPassword') || 'Set New Password')}
          </Typography>
          {welcome && (
            <Typography sx={{ mt: 0.75, fontSize: '0.92rem', color: TEXT.secondary }}>
              {t('welcomeSetPasswordHint') || 'Choose your password. After that you sign in with your email.'}
            </Typography>
          )}
        </Box>

        {status && (
          <Alert severity={status} sx={{ mb: 2 }}>{message}</Alert>
        )}

        {status !== 'success' && (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('newPassword') || 'New Password'}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              autoFocus
              required
              InputLabelProps={{ sx: { fontSize: '0.95rem' } }}
              InputProps={{
                sx: { fontSize: '0.95rem' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label={t('confirmPassword') || 'Confirm Password'}
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              sx={{ mb: 3 }}
              required
              InputProps={{ sx: { fontSize: '0.95rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.95rem' } }}
            />
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading || !password || !confirm}
              sx={{ py: 1.5, fontSize: '0.95rem', fontWeight: 600, bgcolor: BRAND.green, '&:hover': { bgcolor: BRAND.greenDark } }}
            >
              {loading ? <CircularProgress size={24} /> : (t('resetPassword') || 'Reset Password')}
            </Button>
          </form>
        )}

        <Typography sx={{ textAlign: 'center', mt: 2, fontSize: '0.9rem' }}>
          <Link to="/login" style={{ color: BRAND.green, fontWeight: 600, display: 'inline-block', padding: '8px 4px' }}>
            {t('backToLogin') || 'Back to Login'}
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
